/**
 * VehicleCard - Card de veiculo para planejamento
 * Exibe informacoes do veiculo e permite selecao
 */

import { memo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  Avatar,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  LocalShipping as TruckIcon,
  TwoWheeler as MotoIcon,
  Person as DriverIcon,
  Speed as SpeedIcon,
  Inventory as CapacityIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const VehicleCard = memo(({
  vehicle,
  selected = false,
  onSelect,
  disabled = false,
  showDetails = true,
}) => {
  const tokens = usePremiumTokens();

  const getVehicleIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'truck':
      case 'caminhao':
        return <TruckIcon />;
      case 'motorcycle':
      case 'moto':
        return <MotoIcon />;
      default:
        return <CarIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'online':
      case 'moving':
        return tokens?.status?.online || '#3fb950';
      case 'idle':
        return tokens?.status?.idle || '#d29922';
      case 'offline':
        return tokens?.status?.offline || '#8b949e';
      default:
        return tokens?.text?.secondary;
    }
  };

  return (
    <Box
      onClick={() => !disabled && onSelect?.(vehicle.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        backgroundColor: selected
          ? `${tokens?.accent?.primary}12`
          : tokens?.surface?.raised,
        borderRadius: tokens?.radius?.md || '8px',
        border: `2px solid ${selected ? tokens?.accent?.primary : 'transparent'}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: tokens?.transitions?.all,
        '&:hover': {
          backgroundColor: disabled
            ? undefined
            : selected
              ? `${tokens?.accent?.primary}18`
              : tokens?.surface?.hover,
        },
      }}
    >
      {/* Checkbox */}
      <Checkbox
        checked={selected}
        disabled={disabled}
        sx={{
          p: 0.5,
          color: tokens?.text?.tertiary,
          '&.Mui-checked': {
            color: tokens?.accent?.primary,
          },
        }}
      />

      {/* Avatar do veiculo */}
      <Avatar
        sx={{
          width: 40,
          height: 40,
          backgroundColor: selected
            ? tokens?.accent?.primary
            : tokens?.surface?.elevated,
          color: selected
            ? '#fff'
            : tokens?.text?.secondary,
        }}
      >
        {getVehicleIcon(vehicle.category)}
      </Avatar>

      {/* Informacoes */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            {vehicle.name}
          </Typography>

          {/* Status indicator */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getStatusColor(vehicle.status),
            }}
          />
        </Box>

        <Typography
          sx={{
            color: tokens?.text?.secondary,
            fontSize: '12px',
          }}
        >
          {vehicle.plate || vehicle.uniqueId}
        </Typography>

        {/* Detalhes */}
        {showDetails && (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            {vehicle.driver && (
              <Chip
                icon={<DriverIcon sx={{ fontSize: '12px !important' }} />}
                label={vehicle.driver}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: tokens?.surface?.elevated,
                  color: tokens?.text?.secondary,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            )}

            {vehicle.capacity && (
              <Chip
                icon={<CapacityIcon sx={{ fontSize: '12px !important' }} />}
                label={`${vehicle.capacity} kg`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: tokens?.surface?.elevated,
                  color: tokens?.text?.secondary,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            )}

            {vehicle.speed !== undefined && (
              <Chip
                icon={<SpeedIcon sx={{ fontSize: '12px !important' }} />}
                label={`${Math.round(vehicle.speed)} km/h`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '10px',
                  backgroundColor: tokens?.surface?.elevated,
                  color: tokens?.text?.secondary,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

VehicleCard.displayName = 'VehicleCard';

export default VehicleCard;
